import { autoinject, BindingEngine, bindingMode, Disposable, ICollectionObserverSplice } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall, ApiPostResponse } from 'app/shared/lib/essette-api';
import { EmailService } from 'app/core/services/email-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { UserService } from 'app/core/services/user-service';
import { deepClone, pushCopy } from 'app/shared/lib/utilities';
import { guid } from 'app/shared/lib/guid';
import { CopyOptions, CustomEmailTemplateRecipient } from 'app/core/components/email-notifications/email-notifications-recipients';
import { spliceCopy } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import { I18N } from 'aurelia-i18n';
import * as Core from 'app/core/models/essette-api';

/**
 * Filters copy options based on values already assigned to that recipient user.
 * @param options - The array of options to filter.
 * @param recipient - The recipient under consideration.
 * @param allUserRecipients - The array of all user recipients to compare against.
 */
export class FilterCopyOptionsPerUserValueConverter {
    toView<T>(
        options: string[],
        recipient: Core.EmailTemplateRecipient,
        allUserRecipients: Core.EmailTemplateRecipient[]
    ): string[] {
        if(!recipient || !allUserRecipients) return options;
        // Find all instances of this user already assigned to this email template...
        const foundUsers = allUserRecipients.filter(r => (r.user && r.user.id) === (recipient.user && recipient.user.id));
        // ... if none found, all options are available
        if(!foundUsers.length) return options;
        // Now collect all assigned copy options...
        const assignedCopyOptions = new Set(foundUsers.map(user => user.copy && user.copy.toLowerCase()));
        // ... then filter out copy options that are already assigned to user
        return options.filter(option => !assignedCopyOptions.has(option.toLowerCase()));
    }
}

@autoinject()
export class EmailNotificationsSelected extends ComponentBase implements IForm {
    // enable the use of the FilterCopyOptionsPerUserValueConverter in this component's view
    static $viewResources = [FilterCopyOptionsPerUserValueConverter];

    ////****************** Fields and Properties ************************************////

    @bindable.objectNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected emailTemplate: Core.EmailTemplate | null = null;

    // List of all existing user recipients to filter out from users dropdown
    protected allUserRecipients: Core.EmailTemplateRecipient[] = [];

    // List of selected Users from users dropdown
    protected selectedUsers: CustomEmailTemplateRecipient[] = [];

    // List of new Recipients to add
    public newRecipients: CustomEmailTemplateRecipient[] = [];

    // Custom Recipient Email to add
    protected customUserEmail: string = '';

    // Misc
    // For loading users from dropdown
    protected users: CustomEmailTemplateRecipient[] = [];
    // For selected Recipient type to add
    protected addRecipientOption: string = 'selectUsers';

    // Copy options for copy dropdown
    protected copyOptions = CopyOptions;

    protected maxEmailLength: number = 200;
    protected maxDescriptionLength: number = 200;

    protected validate = validate;
    protected isCustomUserEmailFormValid: boolean = false;
    protected selectedUsersSubscription: Disposable | null = null;

    protected id: guid = guid();

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly emailService: EmailService,
        private readonly userService: UserService,
        private readonly toaster: Toaster,
        private readonly bindingEngine: BindingEngine,
        protected readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected attached(): void {
        this.observeSelectedUsers();
    }

    protected detached(): void {
        if(this.selectedUsersSubscription) this.selectedUsersSubscription.dispose();
    }

    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        this.reset();
        await this.reloadInformation();
    }

    protected selectedUsersChanged(splices: ICollectionObserverSplice<CustomEmailTemplateRecipient>[]): void {
        if(this.newRecipients.length === 0 && this.selectedUsers.length === 0) return;

        for(let i = 0; i < splices.length; i++) {
            const splice = splices[i];

            const recipientsAdded = this.selectedUsers.slice(splice.index, splice.index + splice.addedCount);
            if(recipientsAdded.length > 0) {
                recipientsAdded.forEach(recipient => {
                    this.newRecipients = pushCopy(this.newRecipients, recipient);
                });
            }

            if(splice.removed.length) {
                (splice.removed as CustomEmailTemplateRecipient[]).forEach(recipient => {
                    const i = this.newRecipients.find(r => (r.user && r.user.id) === (recipient.user && recipient.user.id));
                    if(i) this.newRecipients = spliceCopy(this.newRecipients, this.newRecipients.indexOf(i), 1);
                });
            }
        }
    }

    ////****************** Functions ************************************************////
    // filter out users who already have all three copy types assigned
    protected filterUsers = (user: CustomEmailTemplateRecipient): boolean => {
        const foundUserRecipients = this.allUserRecipients.filter(r => (r.user && r.user?.id) === (user.user && user.user.id));
        if(foundUserRecipients.length) {
            const copyTypes = new Set(foundUserRecipients.map(r => r.copy?.toLowerCase()));
            if(copyTypes.has('to') && copyTypes.has('cc') && copyTypes.has('bcc')) return false;
        }
        return true;
    }

    protected observeSelectedUsers(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedUsersSubscription) this.selectedUsersSubscription.dispose();

        this.selectedUsersSubscription = this.bindingEngine
            .collectionObserver(this.selectedUsers) // observe the selectedUsers array, which changes in the multi-select User Select element
            .subscribe(this.selectedUsersChanged.bind(this)); // pass the splice information (addedCount, index, removed) to selectedUsersChanged method
        // subscription disposed of in detached()
    }

    public reset(): void {
        this.newRecipients = [];
        this.selectedUsers.splice(0);
        this.customUserEmail = '';
        this.addRecipientOption = 'selectUsers';
    }

    public async reloadInformation(reloadUsers: boolean = true): Promise<void> {
        const promises = [this.loadAllUserEmailRecipients()];
        if(reloadUsers) promises.push(this.loadNonInternalUsers());
        await Promise.all(promises);
    }

    public async submit(): Promise<boolean> {
        return this.saveEmailRecipients();
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    protected addCustomRecipient() {
        const recipientToInsert: Core.EmailTemplateRecipient = {
            user: null,
            recipient: {
                description: 'Non-User',
                emailAddress: this.customUserEmail
            },
            emailTemplate: {
                id: this.emailTemplate?.id
            },
            copy: 'To'
        } as Core.EmailTemplateRecipient;

        // need to trigger dataChanged
        this.newRecipients = pushCopy(this.newRecipients, recipientToInsert);

        // Clear Fields
        this.customUserEmail = '';
    }

    // Inserting New Recipients
    public removeNewRecipient(recipient: Core.EmailTemplateRecipient) {
        const index: number = this.newRecipients.indexOf(recipient, 0);

        // need to trigger dataChanged
        this.newRecipients = spliceCopy(this.newRecipients, index, 1);

        const selectedUser = this.selectedUsers.find(r => r.user && r.user.id === (recipient.user && recipient.user.id));
        if(selectedUser) this.selectedUsers.splice(this.selectedUsers.indexOf(selectedUser), 1);
    }

    protected copyApplyToAll(copy: string) {
        this.newRecipients.forEach(r => {
            // Only apply copy if user does not already have this copy type
            const userId = r.user && r.user.id;
            if(!userId || !copy) {
                r.copy = copy;
                return;
            }
            const found = this.allUserRecipients.some(rec => (rec.user && rec.user.id) === userId && (rec.copy && rec.copy.toLowerCase() === copy.toLowerCase()));
            if(!found) r.copy = copy;
        });
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    // Load all recipients to filter out from users dropdown
    private async loadAllUserEmailRecipients(): Promise<void> {
        if(!this.emailTemplate?.id) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false
        };

        const searchFilters = {
            usersOnly: true
        };

        const fields = [
            'copy',
            'user.id'
        ];

        try {
            this.state.beginLoad();

            const response = await this.emailService.loadEmailRecipientsByEmailTemplate(this.emailTemplate?.id, options, [], searchFilters, fields);
            this.allUserRecipients = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async loadNonInternalUsers(): Promise<void> {
        const includes: string[] = [];

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false
        };

        try {
            this.state.beginLoad();

            // Load only users with emails
            const searchFilter = {
                hasEmail: true,
                hasLogin: true
            }
            const response = await this.userService.loadUsers(options, includes, searchFilter);
            const users = response.results || null;

            // Map necessary data to prepare for API insert post
            this.users = users ? users.map(user => {
                // Determine available copy options for this user
                const foundUserInstances = this.allUserRecipients.filter(r => r.user?.id === user.id);
                const assignedCopies = new Set(foundUserInstances.map(r => r.copy?.toLowerCase()));
                const availableCopies = this.copyOptions.filter(opt => !assignedCopies.has(opt.toLowerCase()));
                let defaultCopy = null;
                if(availableCopies.includes('To')) {
                    defaultCopy = 'To';
                } else if(availableCopies.length > 0) {
                    defaultCopy = availableCopies[0];
                }
                return {
                    displayDescription: user.description + (user.code ? ' (' + user.code + ')' : ''),
                    recipient: {
                        description: user.description,
                        emailAddress: user.emailAddress
                    },
                    user: {
                        id: user.id,
                        login: {
                            id: user.login?.id
                        },
                        description: user.description,
                        code: user.code,
                    },
                    emailTemplate: {
                        id: this.emailTemplate?.id
                    },
                    copy: defaultCopy
                } as Core.EmailTemplateRecipient
            }) : [];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveEmailRecipients(): Promise<boolean> {
        const promises = new Array<Promise<ApiPostResponse>>();

        const recipientsToInsert = deepClone(this.newRecipients);
        recipientsToInsert.forEach(recipient => delete recipient.displayDescription); // remove custom display property to allow insert

        // Check for duplicate copy types for users (edge case)
        let duplicateFound = false;
        recipientsToInsert.forEach(recipient => {
            if(recipient.user) {
                // Check if this user already has this copy type in allUserRecipients
                const alreadyExists = this.allUserRecipients.some(r =>
                    (r.user && r.user.id) === (recipient.user && recipient.user.id) &&
                    (r.copy && r.copy.toLowerCase()) === (recipient.copy && recipient.copy.toLowerCase())
                );
                if(alreadyExists) duplicateFound = true;
            }
        });
        if(duplicateFound) {
            this.toaster.error().message(this.i18n.tr('core:local.emailNotifications.copyTypeExists')).show();
            return false;
        }

        //Go through list of recipients to insert
        recipientsToInsert.forEach(recipient => {
            if(recipient.user) {
                recipient.recipient = undefined;
            }
            else {
                recipient.user = undefined;
            }

            recipient.copy = recipient.copy?.toLowerCase();

            promises.push(this.emailService.insertEmailTemplateRecipient(recipient));
        });

        let isSuccessful: boolean = true;
        try {
            this.state.beginSave();
            const responses = await Promise.all(promises);

            isSuccessful = responses.every((r) => r.isSuccessful);
            return isSuccessful;
        }
        catch(error) {
            isSuccessful = false;
            this.reset();
            throw error;
        }
        finally {
            this.toaster.standard.saved('core:emailTemplate.recipient.$', isSuccessful, recipientsToInsert.length);
            this.state.endSave();
        }
    }

}
